import classNames from "classnames";
import * as React from "react";
import * as Ons from "react-onsenui";
import { isLoading, Loading } from "../Loading";
import {
  CaregiverPatientDocument,
  CaregiverPatientDocumentType,
  PatientDocumentScanCreate,
} from "../messages/PatientDocuments";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { CAMERA_SVG, NOTES_SVG, TIMES_SVG, VISIT_TYPE_SVG } from "../utils/svgs";
import {
  isDocumentNotSubmitted,
  isDocumentSubmitted,
  isDocumentSubmittedManually,
} from "./caregiverPatientDocumentUtils";
import DocumentUploadActionsActivity from "./DocumentUploadActionsActivity";
import { AppButton } from "./Generic/AppButton";
import AppLoadingPlaceholder from "./Generic/AppLoadingPlaceholder";
import { simpleAlert } from "./SimpleAlert";
import { InitialPlanOfCare } from "./useVisitInstancePlanOfCare";

export interface ScheduledVisitReqDocsComponentProps {
  documents: CaregiverPatientDocument[] | Loading;
  planOfCare: InitialPlanOfCare | null | Loading;
  isUploadingDocument: boolean;
  onUploadFreeScan: (data: PatientDocumentScanCreate) => void;
  onDeleteFreeScan: (document: CaregiverPatientDocument.FreeScan) => void;
  onClickDocument: (document: CaregiverPatientDocument) => void;
  onClickPlanOfCare: (planOfCare: InitialPlanOfCare) => void;
}

interface DocumentStatusParams {
  hasSubmitted: boolean;
  isSubmittedManually: boolean;
}
interface ScheduledVisitReqDocsComponentState {
  isScanningNewDocument: boolean;
}
export class ScheduledVisitReqDocsComponent extends React.Component<
  ScheduledVisitReqDocsComponentProps,
  ScheduledVisitReqDocsComponentState
> {
  constructor(props: ScheduledVisitReqDocsComponentProps) {
    super(props);

    this.state = {
      isScanningNewDocument: false,
    };
  }

  handleCloseDocument = () => this.setState({ isScanningNewDocument: false });

  renderDocStatus({ hasSubmitted, isSubmittedManually }: DocumentStatusParams) {
    if (isSubmittedManually) {
      return (
        <div className="ScheduledVisitReqDocsComponent__itemUploaded">
          <img src="assets/patient-document-done.svg" />
          <span>{translate(STRINGS.SUBMITTED_MANUALLY)}</span>
        </div>
      );
    }

    if (hasSubmitted) {
      return (
        <div className="ScheduledVisitReqDocsComponent__itemUploaded">
          <img src="assets/patient-document-done.svg" />
          <span>{translate(STRINGS.UPLOADED)}</span>
        </div>
      );
    }

    return (
      <div className="ScheduledVisitReqDocsComponent__itemMissing">
        <span>{translate(STRINGS.MISSING)}</span>
      </div>
    );
  }

  renderReqDocComponent = (doc: CaregiverPatientDocument) => {
    return (
      <div
        key={doc.title}
        className={classNames({
          ScheduledVisitReqDocsComponent__item: true,
          ScheduledVisitReqDocsComponent__itemDone:
            "submittedAt" in doc && doc.submittedAt !== null,
        })}
        onClick={() => this.props.onClickDocument(doc)}
      >
        {doc.type === CaregiverPatientDocumentType.FreeScan ? (
          <div
            className="ScheduledVisitReqDocsComponent__itemIcon dismiss"
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              this.props.onDeleteFreeScan(doc);
            }}
          >
            {TIMES_SVG}
          </div>
        ) : (
          <div className="ScheduledVisitReqDocsComponent__itemIcon note">{NOTES_SVG}</div>
        )}
        <div className="ScheduledVisitReqDocsComponent__itemTitle">{doc.title}</div>
        {this.renderDocStatus({
          hasSubmitted: isDocumentSubmitted(doc),
          isSubmittedManually: isDocumentSubmittedManually(doc),
        })}
      </div>
    );
  };

  renderPlanOfCare() {
    const planOfCare = this.props.planOfCare;

    if (isLoading(planOfCare) || planOfCare === null) {
      return null;
    }

    return (
      <div
        className={classNames({
          ScheduledVisitReqDocsComponent__item: true,
          ScheduledVisitReqDocsComponent__itemDone:
            planOfCare.initialPlanOfCare.poc.submittedAt !== null,
        })}
        onClick={() => {
          this.props.onClickPlanOfCare(planOfCare);
        }}
      >
        <div className="ScheduledVisitReqDocsComponent__itemIcon note">{VISIT_TYPE_SVG}</div>
        <span className="ScheduledVisitReqDocsComponent__itemTitle">
          {translate(STRINGS.PLAN_OF_CARE)}
        </span>
        {this.renderDocStatus({
          hasSubmitted: planOfCare.initialPlanOfCare.poc.submittedAt !== null,
          isSubmittedManually: false,
        })}
      </div>
    );
  }

  renderNewFreescanButton() {
    const { isScanningNewDocument } = this.state;
    const onScanningNewDocumentClick = () => {
      this.setState({ isScanningNewDocument: true });
    };

    const onScanSuccessCallback = (data: PatientDocumentScanCreate) => {
      this.props.onUploadFreeScan(data);
    };

    const onScanErrorCallback = (message: string) => {
      switch (message) {
        case "Scan was cancelled": {
          return;
        }
        default: {
          simpleAlert({
            icon: "error",
            message: translate(STRINGS.UPLOAD_ERROR),
            title: translate(STRINGS.ERROR),
            cancelable: true,
          });
        }
      }
    };

    return (
      <div className="ScheduledVisitReqDocsComponent__itemNew">
        <AppButton variant="outline" color="primary" onClick={onScanningNewDocumentClick}>
          {CAMERA_SVG}
          <span>{translate(STRINGS.UPLOAD_DOCUMENTS)}</span>
        </AppButton>
        <DocumentUploadActionsActivity
          isDocumentNameNeeded={true}
          isScanningNewDocument={isScanningNewDocument}
          handleCloseDocumentNameDialog={this.handleCloseDocument}
          onScanSuccessCallback={onScanSuccessCallback}
          onScanErrorCallback={onScanErrorCallback}
        />
      </div>
    );
  }

  renderDocs() {
    if (isLoading(this.props.documents)) {
      return null;
    }

    return (
      <div className="ScheduledVisitReqDocsComponent__docsContainer">
        {this.props.documents.length > 0 ? (
          <>
            <div className="ScheduledVisitReqDocsComponent__docsList">
              {this.props.documents.map(this.renderReqDocComponent)}
            </div>
            {this.renderPlanOfCare()}
            <hr className={"ScheduledVisitReqDocsComponent_preFreescanDivider"} />
            {this.renderNewFreescanButton()}
          </>
        ) : (
          <>
            <span>{translate(STRINGS.NO_DOCUMENTS_REQUIRED)}</span>
          </>
        )}
      </div>
    );
  }

  render() {
    if (isLoading(this.props.documents) || isLoading(this.props.planOfCare)) {
      return (
        <div className={"ScheduledVisitReqDocsComponent__loading"}>
          <Ons.ProgressCircular indeterminate={true} />
        </div>
      );
    } else {
      const planOfCare = this.props.planOfCare;
      const unfinishedDocsAmount =
        this.props.documents.filter(isDocumentNotSubmitted).length +
        (planOfCare !== null && planOfCare.initialPlanOfCare.poc.submittedAt === null ? 1 : 0);

      return (
        <>
          {unfinishedDocsAmount > 0 ? (
            <div className="ScheduledVisitReqDocsComponent__remainingContainer">
              <span>{unfinishedDocsAmount + " " + translate(STRINGS.REMAINING)}</span>
            </div>
          ) : null}
          {this.renderDocs()}
          <AppLoadingPlaceholder
            primaryHeader={translate(STRINGS.UPLOADING)}
            secondaryHeader={translate(STRINGS.PLEASE_WAIT) + "..."}
            isOpen={this.props.isUploadingDocument}
          />
        </>
      );
    }
  }
}
